import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api-client'
import { ApiError } from '@/lib/api-error'
import { queryKeys } from '@/lib/query-keys'
import type { IUser } from '@/types/user'

/**
 * Session probe against `/secure/user/profile`.
 *
 * The endpoint returns the user document as the response body itself rather than
 * wrapping it in `{ data }` or `{ user }` -- worth stating because the shape is
 * inconsistent with every other endpoint in this API.
 *
 * `skipUnauthorizedHandler` is set because a 401 here is the expected answer for
 * a signed-out operator, not an event that should trigger the global redirect.
 * A 401 whose body sets `requires2fa` fails the same way: the cookie is present,
 * but this network has not entered a code. Callers tell the two apart from the
 * error. It must not trip the global redirect either.
 */
export function useSession() {
    return useQuery({
        queryKey: queryKeys.session,
        queryFn: ({ signal }) => api.get<IUser>('/secure/user/profile', { signal, skipUnauthorizedHandler: true }),
        retry: (failureCount, error) => {
            if (error instanceof ApiError && (error.isUnauthenticated || error.isForbidden)) return false
            return failureCount < 1
        },
        // The session outlives any single view; re-probing on every mount would add
        // a request to each navigation for information that rarely changes.
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false
    })
}
